import React, { useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import BackButton from '../../Components/BackButton';
import { useCachedGet } from '../../Services/useCachedGet';
import { getEntityTypeStyle } from '../../models/entity.enum';
import { gsap, SplitText, useGSAP, prefersReducedMotion } from '../../lib/gsap';
import { useReveal } from '../../Components/Fx/useReveal';

const humanizeKey = (key: string) => key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, c => c.toUpperCase());

const ProfileSkeleton = () => (
   <div className="page animate-pulse pt-32">
      <div className="h-40 w-40 rounded-full bg-ink-700" />
      <div className="mt-8 h-16 w-2/3 rounded-sm bg-ink-700" />
      <div className="mt-4 h-4 w-40 rounded-sm bg-ink-800" />
      <div className="mt-12 h-32 w-full rounded-sm bg-ink-800" />
   </div>
);

interface RelatedEvent {
   id: string;
   title: string;
   cover?: string;
}

interface EntityData {
   id: string;
   name: string;
   type: string;
   thumbnail?: string;
   summary?: string;
   metadata?: Record<string, string>;
   relatedEvents?: RelatedEvent[];
}

const EntityProfilePage = () => {
   const { entityId } = useParams();
   const { data: entity, loading, error } = useCachedGet<EntityData>(
      entityId ? `${process.env.REACT_APP_API}/entity/${entityId}` : null
   );
   const scope = useRef<HTMLDivElement>(null);

   useGSAP(
      () => {
         if (!entity || prefersReducedMotion()) return;
         const split = SplitText.create('[data-entity-name]', {
            type: 'chars',
            mask: 'chars',
            autoSplit: true,
            onSplit: self => gsap.from(self.chars, { yPercent: 110, stagger: 0.02, duration: 1.2 }),
         });
         gsap.from('[data-entity-portrait]', {
            clipPath: 'circle(0% at 50% 50%)',
            duration: 1.8,
            ease: 'expo.inOut',
         });
         return () => split.revert();
      },
      { scope, dependencies: [entity] }
   );
   useReveal(scope, [entity]);

   if (loading) return <ProfileSkeleton />;

   if (error || !entity) {
      return (
         <div className="page py-40 text-center">
            <BackButton />
            <p className="mt-6 font-display text-4xl">Could not find this profile.</p>
         </div>
      );
   }

   const style = getEntityTypeStyle(entity.type);
   const Icon = style.icon;
   const details = Object.entries(entity.metadata || {}).filter(([, value]) => !!value);

   return (
      <div ref={scope} className="relative">
         <div className={`pointer-events-none absolute inset-x-0 top-0 h-[70vh] bg-gradient-to-b ${style.accent}`} />

         <div className="page relative pt-28">
            <BackButton />

            <header className="mt-10 grid grid-cols-1 items-end gap-10 md:grid-cols-12">
               <div className="md:col-span-4">
                  <div data-entity-portrait className="relative aspect-square w-48 overflow-hidden rounded-full md:w-full md:max-w-[320px]">
                     {entity.thumbnail ? (
                        <img
                           src={entity.thumbnail}
                           alt={entity.name}
                           className="h-full w-full object-cover grayscale contrast-125 transition duration-700 hover:grayscale-0"
                        />
                     ) : (
                        <div className="flex h-full w-full items-center justify-center bg-ink-800">
                           <Icon size={56} className="text-bone-dim" />
                        </div>
                     )}
                     <div className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-inset ring-white/15" />
                  </div>
               </div>
               <div className="md:col-span-8">
                  <span className={`chip mb-6 ${style.badge}`}>
                     <Icon size={11} />
                     {style.label}
                  </span>
                  <h1 data-entity-name className="font-display text-6xl leading-[0.9] tracking-tightest md:text-8xl">
                     {entity.name}
                  </h1>
               </div>
            </header>

            <div className="rule my-16" />

            <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
               <div className="lg:col-span-7">
                  {entity.summary && (
                     <>
                        <p className="eyebrow mb-6" data-reveal>
                           Profile
                        </p>
                        <p className="font-display text-2xl leading-snug text-bone md:text-3xl" data-reveal>
                           {entity.summary}
                        </p>
                     </>
                  )}
               </div>

               {details.length > 0 && (
                  <dl className="lg:col-span-5">
                     <p className="eyebrow mb-6" data-reveal>
                        Record
                     </p>
                     {details.map(([key, value]) => (
                        <div key={key} className="flex justify-between gap-6 border-b border-white/[0.07] py-4" data-reveal>
                           <dt className="font-mono text-[11px] uppercase tracking-[0.18em] text-bone-dim">
                              {humanizeKey(key)}
                           </dt>
                           <dd className="text-right text-bone">{value}</dd>
                        </div>
                     ))}
                  </dl>
               )}
            </div>

            {entity.relatedEvents && entity.relatedEvents.length > 0 && (
               <section className="mt-28">
                  <h2 className="headline" data-reveal>
                     Related cases
                  </h2>
                  <ul className="border-t border-white/[0.07]">
                     {entity.relatedEvents.map((related, i) => (
                        <li key={related.id} data-reveal>
                           <Link
                              to={`/event/${related.id}`}
                              className="group flex items-center gap-6 border-b border-white/[0.07] py-6 transition-colors hover:bg-white/[0.02]"
                           >
                              <span className="font-mono text-xs text-signal">{String(i + 1).padStart(2, '0')}</span>
                              {related.cover && (
                                 <img
                                    src={related.cover}
                                    alt=""
                                    className="h-16 w-24 flex-shrink-0 rounded-sm object-cover grayscale transition group-hover:grayscale-0"
                                 />
                              )}
                              <span className="flex-1 font-display text-2xl text-bone transition-transform duration-500 group-hover:translate-x-2 md:text-3xl">
                                 {related.title}
                              </span>
                              <span className="text-bone-dim transition-colors group-hover:text-signal">→</span>
                           </Link>
                        </li>
                     ))}
                  </ul>
               </section>
            )}
         </div>
      </div>
   );
};

export default EntityProfilePage;
